// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * modes.ts — the three shapes a run can take.
 *
 * Principle #14: a mode must change how the game PLAYS, not just a number. The
 * three here move the two things that actually reshape a run — how much MAP
 * there is, and how much of it the warmth can ever reach:
 *
 *   Thaw       small map, wide warmth. Almost everything is reachable, so the
 *              run is about sequencing and food, not about territory. This is
 *              the one where you can afford to sprawl.
 *   Frontier   big map, moderate warmth. Half the map is out of reach at any
 *              moment, so stoking to open ground is a real decision and scouts
 *              have somewhere to go. The default shape.
 *   Longnight  big map, warmth radius ONE. You physically cannot use the outer
 *              map early, so it inverts into a siege: build dense on top of the
 *              generator, and buy every tile of ring outward with coal. Caches
 *              are richer to compensate, so scouting stops being optional.
 *
 * The host's pick travels frozen inside the round start (`roundOpts`), never
 * read from each peer's own UI — see race.ts.
 */

export interface Mode {
  readonly id: string;
  readonly name: string;
  readonly blurb: string;
  /** Map radius in hexes. The map is the disc of cells within this of the generator. */
  readonly radius: number;
  /** Days you must survive to win. */
  readonly days: number;
  /** Starting generator heat. The warmth RADIUS is floor(heat). */
  readonly startHeat: number;
  /** Heat lost on night 1 by a ring of radius 0. */
  readonly coldBase: number;
  /** Extra heat lost per night thereafter — the cold getting worse. */
  readonly coldSlope: number;
  /**
   * Extra heat lost per tile of RING. A big ring bleeds more than a small one.
   *
   * This is the load-bearing constant of the whole economy and it was put here
   * by the sim, not by argument. Without it the bite is flat, which means a
   * small ring costs exactly as much to hold as a large one — so warmth-first
   * strictly dominated (Longnight measured 78% against 37% and 42% for the
   * other two lines), and Longnight's radius-1 opening collapsed to radius ZERO
   * on night 3 with no warning, wiping every building at once. Making the cold
   * scale with the ring fixes both at once: a small ring is cheap to hold so
   * the opening stops being a cliff, and a big ring is a standing bill rather
   * than a one-off purchase, so buying territory early is no longer free.
   *
   * It adds no state — it is a pure function of (day, warmth) — so nothing about
   * determinism or the same-seed race changes.
   */
  readonly ringCost: number;
  /** Hexes revealed around the generator at the start. */
  readonly startVision: number;
  /**
   * Scales what a scout brings home. Longnight leans on this: if you cannot
   * expand into the map, the map has to come to you.
   */
  readonly cacheRichness: number;
  readonly startPop: number;
  readonly startWood: number;
  readonly startFish: number;
  readonly startCoal: number;
}

/** Shared economy. These are the game's rules, not a mode's dial. */
export const COST = {
  home: 3,
  hut: 2,
  workshop: 5,
  /** Coal burned per stoke. */
  stokeCoal: 3,
  /** Heat one stoke adds. */
  stokeHeat: 1,
} as const;

/** People a hearth-home shelters, if it is inside the warmth at nightfall. */
export const SHELTER_PER_HOME = 2;
/** A staffed hut inside the warmth yields this much of its tile's resource. */
export const HUT_YIELD = 2;
/** Extra yield a hut gets for each workshop it touches. */
export const WORKSHOP_BONUS = 1;
/** Fish each person eats per night. */
export const FOOD_PER_PERSON = 1;
/**
 * Nights a scout is away. One: you send them at dusk, they walk, and they are
 * home by dawn with whatever they found.
 *
 * This was 2, and the sim says it cannot be. The economy was tuned against a
 * scout costing ONE worker-night, but it only ever cost that because of a bug:
 * a scout was credited back to the labour pool on the night they walked home,
 * so a two-night trip was billed as one. Fixing that (a player noticed they
 * could "gather with more people than I had") made the real price visible and
 * the explore line collapsed to 21-25% against a 35% viability floor.
 *
 * The choice was to make the trip honestly cost two nights and pay for it with
 * richer caches, or to make the trip genuinely one night. Measured over 200
 * runs per cell, paying for it does not work: with density at its documented
 * third, explore saturates at 39-44% no matter how rich the caches get (tested
 * to 3x), because the binding constraint is labour, not loot. Buying it back
 * with density AND richness together looked fine at 120 runs and failed at 200
 * on the spread gate. A one-night trip passes every gate on the first try with
 * nothing else touched, and lands explore at 59-70% — the band it originally
 * measured. So this restores the intended economy rather than inventing a new
 * one to sit on top of the bug.
 */
export const SCOUT_NIGHTS = 1;

export const MODES: readonly Mode[] = [
  {
    id: 'thaw',
    name: 'Thaw',
    blurb: 'Small map, wide warmth. Room to breathe — and nowhere to hide from a bad night.',
    radius: 3,
    days: 9,
    startHeat: 2.9,
    coldBase: 0.2,
    coldSlope: 0.088,
    ringCost: 0.078,
    startVision: 2,
    cacheRichness: 1,
    startPop: 4,
    startWood: 11,
    startFish: 9,
    startCoal: 5,
  },
  {
    id: 'frontier',
    name: 'Frontier',
    blurb: 'A wide frontier and a ring that only half covers it. Stoke outward, or stay small.',
    radius: 4,
    days: 10,
    startHeat: 2.7,
    coldBase: 0.2,
    coldSlope: 0.075,
    ringCost: 0.085,
    startVision: 2,
    cacheRichness: 1.15,
    startPop: 4,
    startWood: 11,
    startFish: 9,
    startCoal: 6,
  },
  {
    id: 'longnight',
    name: 'Longnight',
    blurb: 'Warmth radius ONE. You cannot spread — you have to buy every tile of ring with coal.',
    radius: 4,
    days: 12,
    startHeat: 1.9,
    coldBase: 0.14,
    coldSlope: 0.055,
    ringCost: 0.1,
    startVision: 3,
    cacheRichness: 1.6,
    startPop: 4,
    startWood: 14,
    startFish: 11,
    startCoal: 10,
  },
];

export const DEFAULT_MODE = MODES[1];

/**
 * Look up a mode by id, falling back to the default.
 *
 * `Object.hasOwn`-style guarding rather than `MODES[id] || DEFAULT`: an id off
 * the wire is untrusted, and a plain object lookup would let 'constructor' or
 * 'toString' through as a truthy non-Mode whose every field is undefined. That
 * then reaches the generator as `radius: undefined` and the map comes out empty
 * — a peer crash driven by a string another peer chose.
 */
export function modeOf(id: string | null | undefined): Mode {
  if (typeof id !== 'string') return DEFAULT_MODE;
  const found = MODES.find((m) => m.id === id);
  return found ?? DEFAULT_MODE;
}

/**
 * Heat the night of `day` (1-based) takes off a generator holding `ring` tiles.
 * Worse every night, and worse the more ground you are trying to keep warm.
 */
export function coldFor(mode: Mode, day: number, ring: number): number {
  return mode.coldBase + mode.coldSlope * (day - 1) + mode.ringCost * Math.max(0, ring);
}
